import React, { FC, ReactNode, useEffect } from "react";
import classNames from "classnames";
import { useSelect, UseSelectProps } from "downshift";

import { FieldStatus } from "../../Helpers/FieldStatus";
import { interpolate as inter } from "../../Helpers";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { SpecializationDto } from "../../../../../Api/Model/SpecializationDto.csharp";
import { ServiceDto } from "../../../../../Api/Model/ServiceDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { CommonBookingLabelsDto } from "../../../../../Api/Model/CommonBookingLabelsDto.csharp";
import { intersect } from "../../../Helpers/setsOperationsHelpers";
import { doctorInitialState } from "../../Helpers/initialState/doctorInitialState";

type LocationSelectProps = {
  locations: UseSelectProps<LocationDto | null>;
  label: string;
  placeholder: string;
  onSelect: (location: LocationDto) => void;
  subHeadingFunction?: (forField: string) => string;
  unavailableLocations: {
    items: LocationDto[];
  };
  status?: FieldStatus;
  error?: {
    message?: string;
  };
  dependencies?: string[];
  defaultValue?: LocationDto;
  doctor: DoctorDto;
  onDoctorSelect: (doctor: DoctorDto) => void;
  service: ServiceDto;
  specialization: SpecializationDto;
  additionalLabels: CommonBookingLabelsDto;
};

export const LocationSelect: FC<LocationSelectProps> = ({
  locations,
  label,
  placeholder,
  onSelect,
  unavailableLocations = {
    items: [],
  },
  status = FieldStatus.active,
  error,
  dependencies = [],
  defaultValue = undefined,
  doctor,
  onDoctorSelect,
  service,
  specialization,
  additionalLabels,
}) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect(locations);

  useEffect(() => {
    selectItem(null);
  }, [...dependencies]);

  useEffect(() => {
    const shouldResetDoctor =
      !!selectedItem &&
      ((!!selectedItem.id &&
        selectedItem.isLocation &&
        intersect(doctor.centers, selectedItem.centers).length == 0) ||
        (!selectedItem.isLocation &&
          !doctor.centers.includes(selectedItem.id)));
    if (shouldResetDoctor) {
      onDoctorSelect(doctorInitialState);
    }

    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem);
    }
  }, [selectedItem]);

  function getUnavailableLocationsSubheading() {
    if (
      doctor.id &&
      doctor.id != "-1" &&
      doctor.services.indexOf(service.id) == -1
    )
      return inter(
        additionalLabels.locationNotByDoctor,
        "$doctor",
        doctor.firstName
      );
    if (service.id)
      return inter(
        additionalLabels.locationNotByService,
        "$service",
        service.bookingName
      );
    if (specialization.id)
      return inter(
        additionalLabels.locationNotBySpec,
        "$specialization",
        specialization.name
      );
    return "";
  }

  const getLabelForOnlyOneCenter = (item: LocationDto | null) => {
    if (item?.concatCenterLocationInLocationDropdown) {
      const location = locations.items.find((l) => l?.id == item.locationId);

      return `${item.name} (${location?.name})`;
    }

    return item?.name;
  };

  const mapAvailableLocations = (
    item: LocationDto | null,
    index: number
  ): ReactNode => {
    let label = item?.name;
    const isLocation = item?.isLocation && item?.id != "-1";

    if (isLocation) {
      const centers = locations.items.filter((x) => x?.locationId == item.id);

      if (centers.length < 2) {
        return <div key={item!.id} className="select__optionGroupHeader" />;
      }

      label = `${item!.name} (alle)`;
    }

    const onlyOneCenterInLocation =
      item?.isLocation == false &&
      locations.items.filter((l) => l?.locationId == item.locationId).length ==
        1;

    if (onlyOneCenterInLocation) {
      label = getLabelForOnlyOneCenter(item);
    }

    return (
      <li
        key={item!.id}
        className={classNames("select__option p-1", {
          select__optionGroupHeader:
            item?.isLocation || onlyOneCenterInLocation,
          "select__option--highlight": highlightedIndex === index,
        })}
        {...getItemProps({ item, index })}
      >
        {label}
      </li>
    );
  };

  const mapRestOptionsElements = (item: LocationDto | null) => {
    return (
      <li key={item!.id} className={"select__option p-1"}>
        {item!.name}
      </li>
    );
  };

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  const selectedValueLabel = selectedItem?.name ?? defaultValue?.name ?? "";

  return (
    <div
      className={classNames(
        "position-relative my-2 select booking-narrow-item",
        {
          "select--disabled": status == FieldStatus.disabled,
          "select--error": status == FieldStatus.error,
        }
      )}
    >
      <button
        className="ps-1 py-1 pe-3 select__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {status == FieldStatus.disabled && madeSelection ? (
          <LoadingAnimation text={selectedValueLabel} />
        ) : (
          selectedValueLabel
        )}
      </button>
      <span
        {...getLabelProps()}
        className={classNames("position-absolute select__label", {
          "select__label--focus": madeSelection,
        })}
      >
        {madeSelection && label}
        {!madeSelection && status == FieldStatus.disabled && (
          <LoadingAnimation text={placeholder} />
        )}
        {!madeSelection && status == FieldStatus.active && placeholder}
      </span>
      <ul className="select__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {locations.items.map(mapAvailableLocations)}
            {unavailableLocations.items.length > 0 && (
              <>
                <li className="select__option p-1 pt-2 select__optionGroupHeader unselectable-options-below border-top border-4 border-info">
                  {getUnavailableLocationsSubheading()}
                </li>
                {unavailableLocations.items.map(mapRestOptionsElements)}
              </>
            )}
          </>
        )}
      </ul>
      {status == FieldStatus.error && error && error.message && (
        <span>{error.message}</span>
      )}
    </div>
  );
};
